import { useWarehouse } from "@/contexts/WarehouseContext";
import { useEffect, useState } from "react";
import { FileSpreadsheet, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";

const REPORTS = [
  { id: "voorraad", title: "Actuele voorraad", description: "Alle producten met voorraad, status en minimum." },
  { id: "lage-voorraad", title: "Lage voorraad", description: "Producten onder de minimumvoorraad." },
  { id: "te-bestellen", title: "Te bestellen producten", description: "Wat er direct besteld moet worden." },
  { id: "openstaande-inkopen", title: "Openstaande inkopen", description: "Alle lopende bestellingen." },
  { id: "binnenkomend", title: "Binnenkomende leveringen", description: "Verwachte leveringen komende 7 dagen." },
  { id: "vertraagd", title: "Vertraagde leveringen", description: "Bestellingen met status vertraagd." },
  { id: "voorraad-per-magazijn", title: "Voorraad per magazijn", description: "Voorraad per locatie (webshop: één totaal)." },
  { id: "verkoop", title: "Verkoop en gemiddeld verbruik", description: "Verkoopcijfers per week en maand." },
  { id: "traceability", title: "Batch traceability", description: "Batchhistorie voor voedselveiligheid." },
];

export default function Rapportages() {
  const { warehouse } = useWarehouse();
  const [active, setActive] = useState(REPORTS[0].id);
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/reports/${active}`).then((r) => setData(r.data)); }, [active]);

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="reports-page">
      <PageHeader eyebrow="Inzichten" title="Rapportages"
        description="Bekijk en exporteer rapporten naar Excel of PDF." />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="space-y-2">
          {REPORTS.map((r) => (
            <button key={r.id} onClick={() => setActive(r.id)} data-testid={`report-tab-${r.id}`}
              className={`block w-full text-left p-4 rounded-md border transition-colors ${
                active === r.id ? "border-primary bg-primary/5" : "border-border hover:bg-accent"
              }`}>
              <div className="font-semibold">{r.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{r.description}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 surface overflow-hidden">
          {!data ? (
            <div className="p-12 text-center text-muted-foreground">Laden…</div>
          ) : (
            <>
              <div className="p-6 border-b border-border flex items-center justify-between gap-4">
                <div>
                  <div className="label-eyebrow">Rapport</div>
                  <h3 className="text-xl font-semibold mt-1">{data.title}</h3>
                  <div className="text-sm text-muted-foreground">{data.count} regels</div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`${API}/reports/${active}/export?fmt=xlsx&wh=${warehouse}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="touch-tap" data-testid={`export-xlsx-${active}`}>
                      <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
                    </Button>
                  </a>
                  <a href={`${API}/reports/${active}/export?fmt=pdf&wh=${warehouse}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="touch-tap" data-testid={`export-pdf-${active}`}>
                      <FileText className="w-4 h-4 mr-2" /> PDF
                    </Button>
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>{data.headers.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.rows.slice(0, 200).map((row, i) => (
                      <tr key={`${row[0] ?? "row"}-${i}`}>{row.map((cell, j) => <td key={`${data.headers[j]}-${j}`} className="px-4 py-3 font-mono">{String(cell)}</td>)}</tr>
                    ))}
                    {data.rows.length === 0 && (
                      <tr><td className="p-6 text-center text-muted-foreground" colSpan={data.headers.length}>Geen regels.</td></tr>
                    )}
                  </tbody>
                </table>
                {data.rows.length > 200 && (
                  <div className="p-4 text-center text-xs text-muted-foreground border-t border-border">
                    Eerste 200 regels getoond · exporteer om alle {data.rows.length} te zien.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
